<template>
  <Panel size="small">
    <template #header>
      <span data-cy="heading">Destination</span>
    </template>

    <template #content>
      <div>
        <BaseButtonGroup
          id="buyer-create-campaign__destination__routing"
          :readonly="isProgrammaticBuyerCampaign"
          horizontal
          internal-platform-input
          data-cy="button-group-routing"
          :selected-option="routingType"
          :click-handler="updateRoutingType"
          label="Routing"
          :options="routingOptions"
          has-tooltip
        />

        <fieldset v-if="routingType !== 'Programmatic'">
          <legend class="legend-title">
            Basic Details
          </legend>

          <TargetDestinationForm
            v-if="showTargetDestinationForm"
            :target-details="buyerCampaign.routing"
            @update="updateRouting"
          />

          <RTBDestinationForm
            v-if="routingType === 'RingbaRTB'"
            :target-details="buyerCampaign.routing"
            @update="updateRouting"
          />
        </fieldset>

        <ProgrammaticTargetForm
          v-if="routingType === 'Programmatic'"
          :programmatic-target="programmaticTarget"
          :readonly="readonly"
        />
      </div>
    </template>
  </Panel>
</template>

<script>
import { mapState, mapMutations, mapGetters } from 'vuex'


import TargetDestinationForm from '../Target/TargetDestinationForm.vue'
import ProgrammaticTargetForm from '../ProgrammaticTarget/ProgrammaticTargetForm.vue'
import RTBDestinationForm from './RTBDestinationForm.vue'

const defaultRoutingOptions = [
  {
    text: 'Static',
    value: 'Static'
  }, 
  {
    text: 'Ringba RTB',
    value: 'RingbaRTB'
  }
]

export default {
  name: 'Destination',

  components: {
    ProgrammaticTargetForm,
    TargetDestinationForm,
    RTBDestinationForm
  },

  props: {
    readonly: {
      type: Boolean,
      default: false
    },
  },

  computed: {
    ...mapState('campaign', ['buyerCampaign', 'buyerCampaignStaticLimits']),

    ...mapGetters('campaign', ['isProgrammaticBuyerCampaign']),

    routingOptions() {
      return this.isProgrammaticBuyerCampaign ? Object.freeze([
        ...defaultRoutingOptions,
        {
          text: 'Programmatic',
          value: 'Programmatic'
        }
      ]) : Object.freeze([
        ...defaultRoutingOptions
      ])
    },

    showTargetDestinationForm() {
      return this.routingType === 'Static'
    },

    routingType() {
      return this.buyerCampaign.routing.type
    },

    isNumberDynamic: {
      get() {
        return this.buyerCampaign.routing.programmaticTarget.isNumberDynamic
      },

      set(value) {
        this.updateRouting({
          ...this.buyerCampaign.routing,
          programmaticTarget: {
            ...this.programmaticTarget,
            isNumberDynamic: value
          }
        })
      }
    },

    programmaticTarget() {
      return this.buyerCampaign.routing.programmaticTarget
    }
  },

  methods: {
    ...mapMutations('campaign', [
      'UPDATE_BUYER_CAMPAIGN_ROUTING',
      'UPDATE_BUYER_CAMPAIGN_BUDGET',
      'RESET_BUYER_CAMPAIGN_MINIMUM_DURATION',
      'SET_BUYER_CAMPAIGN_STATIC_LIMITS',
    ]),

    updateRoutingType(option) {
      if (option === this.routingType) {
        return
      }

      const routing = { ...this.buyerCampaign.routing, type: option }

      // Switching off Static clears Max Concurrency, and RTB also clears Minimum Duration and Hourly
      // Budget. Keep the Static values so switching back puts them back: the buyer would otherwise
      // save them unlimited/off without having touched them
      if (this.routingType === 'Static') {
        const { budget } = this.buyerCampaign

        this.SET_BUYER_CAMPAIGN_STATIC_LIMITS({
          concurrencyCap: routing.schedule ? routing.schedule.concurrencyCap : null,
          budget: {
            minimumDurationInSeconds: budget.minimumDurationInSeconds,
            minimumDurationSelected: budget.minimumDurationSelected,
            hourlyBudgetSelected: budget.hourlyBudgetSelected
          }
        })

        // RTB and Programmatic must not inherit the Static Max Concurrency (1 by default),
        // the buyer can still turn it back on manually
        routing.schedule = { ...routing.schedule, concurrencyCap: null }
      }

      const staticLimits = option === 'Static' ? this.buyerCampaignStaticLimits : null

      if (staticLimits) {
        routing.schedule = { ...routing.schedule, concurrencyCap: staticLimits.concurrencyCap }
      }

      this.updateRouting(routing)

      // Only on switching to RTB: RTBDestinationForm edits also go through updateRouting
      // and must not reset the budget the buyer set on RTB
      if (option === 'RingbaRTB') {
        this.RESET_BUYER_CAMPAIGN_MINIMUM_DURATION()
      }

      if (staticLimits) {
        this.UPDATE_BUYER_CAMPAIGN_BUDGET({ ...this.buyerCampaign.budget, ...staticLimits.budget })
        this.SET_BUYER_CAMPAIGN_STATIC_LIMITS(null)
      }
    },

    updateRouting(routing) {
      this.UPDATE_BUYER_CAMPAIGN_ROUTING(routing)
    },

  }
}
</script>

<style lang="scss" scoped></style>
